import { useState } from "react";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import WarningIcon from "@mui/icons-material/Warning";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Link,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import axios from "axios";
import { useParams } from "react-router";

import { orgSessionManager } from "src/domains/auth/services/orgSessionManager";
import { apiBaseUrls } from "src/platform/constants/shared-constants";
import { useAppNavigation } from "src/platform/hooks/useAppNavigation";

const STEPS = ["Connect", "Inspect", "Adopt"];

interface ContractCheck {
  id: string;
  passed: boolean;
  severity: "error" | "warning" | "info";
  message: string;
  autoFixable: boolean;
  fixSuggestion?: string;
}

interface DetectedService {
  name: string;
  type: "server" | "static" | "worker" | "cron";
  port?: number;
  dockerfilePath?: string;
  healthEndpoint?: string;
  internetFacing: boolean;
}

interface RestructuringGuideItem {
  title: string;
  reason: string;
  steps: string[];
  codeSnippet?: string;
  severity: "error" | "warning";
}

interface RepoAnalysis {
  projectType: "single_app" | "monorepo";
  language: string;
  framework: string;
  services: DetectedService[];
  contractChecks: ContractCheck[];
  rushaYml?: string;
  restructuringGuide?: RestructuringGuideItem[];
  repoFullName: string;
  defaultBranch: string;
}

export default function AdoptStackPage() {
  const { orgAlias, projectAlias } = useParams<{
    orgAlias: string;
    projectAlias: string;
  }>();
  const nav = useAppNavigation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [activeStep, setActiveStep] = useState(0);
  const [repoUrl, setRepoUrl] = useState("");
  const [inspecting, setInspecting] = useState(false);
  const [analysis, setAnalysis] = useState<RepoAnalysis | null>(null);
  const [inspectError, setInspectError] = useState<string | null>(null);
  const [stackName, setStackName] = useState("");
  const [deployMode, setDeployMode] = useState("rusha_managed");
  const [adopting, setAdopting] = useState(false);
  const [adoptError, setAdoptError] = useState<string | null>(null);

  const errorChecks =
    analysis?.contractChecks.filter(
      (c) => !c.passed && c.severity === "error",
    ) ?? [];
  const canAdopt = errorChecks.length === 0;

  async function handleInspect() {
    if (!repoUrl.trim()) return;
    setInspecting(true);
    setInspectError(null);
    setAnalysis(null);

    try {
      const response = await axios.post(
        `${apiBaseUrls.BACKEND}/api/v1/applications/inspect`,
        { repoUrl, orgAlias },
        {
          headers: {
            Authorization: `Bearer ${orgSessionManager.getToken() ?? ""}`,
          },
        },
      );
      setAnalysis(response.data as RepoAnalysis);
      setStackName(response.data.repoFullName.split("/")[1] ?? "");
      setActiveStep(1);
    } catch (err) {
      const axiosData = axios.isAxiosError(err)
        ? (err.response?.data as { message?: string })
        : null;
      setInspectError(
        axiosData?.message ??
          (err instanceof Error ? err.message : "Failed to inspect repository"),
      );
    } finally {
      setInspecting(false);
    }
  }

  async function handleAdopt() {
    if (!orgAlias || !projectAlias || !repoUrl.trim()) return;
    setAdopting(true);
    setAdoptError(null);

    try {
      const response = await axios.post(
        `${apiBaseUrls.BACKEND}/api/v1/applications/adopt`,
        {
          repoUrl,
          projectAlias,
          name: stackName || undefined,
          deployMode,
        },
        {
          headers: {
            Authorization: `Bearer ${orgSessionManager.getToken() ?? ""}`,
          },
        },
      );
      const result = response.data as { alias: string };
      nav.goToStackDetails(orgAlias, projectAlias, result.alias);
    } catch (err) {
      const axiosData = axios.isAxiosError(err)
        ? (err.response?.data as { message?: string })
        : null;
      setAdoptError(
        axiosData?.message ??
          (err instanceof Error ? err.message : "Failed to adopt repository"),
      );
    } finally {
      setAdopting(false);
    }
  }

  function handleCancel() {
    nav.goToProjectDetails(orgAlias!, projectAlias!);
  }

  function handleBack() {
    setActiveStep((s) => Math.max(0, s - 1));
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Adopt Existing Project
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Connect an existing GitHub repository. Rusha inspects the code and
        generates a rusha.yml for you.
      </Typography>

      {!isMobile && (
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      )}

      {/* Step 0: Connect */}
      {activeStep === 0 && (
        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Repository URL
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter the GitHub repository you want to adopt.
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              fullWidth
              placeholder="github.com/Rusha-Corp/llm-gateway"
              value={repoUrl}
              onChange={(e) => setRepoUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void handleInspect();
              }}
              sx={{ fontFamily: "monospace" }}
            />
            <Button
              variant="contained"
              onClick={handleInspect}
              disabled={!repoUrl.trim() || inspecting}
              sx={{ minWidth: 160, whiteSpace: "nowrap" }}
            >
              {inspecting ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Inspect Repository"
              )}
            </Button>
          </Stack>

          {inspectError && (
            <Alert
              severity="error"
              sx={{ mt: 2 }}
              onClose={() => setInspectError(null)}
            >
              {inspectError}
            </Alert>
          )}

          <Alert severity="info" sx={{ mt: 3 }}>
            Rusha inspects the repository structure, detects services, and
            validates against project contracts. If restructuring is needed,
            you'll see a clear guide before adoption.
          </Alert>
        </Paper>
      )}

      {/* Step 1: Inspect */}
      {activeStep === 1 && analysis && (
        <Stack spacing={3}>
          {/* Analysis badges */}
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip
              label={
                analysis.projectType === "single_app"
                  ? "Single App"
                  : "Monorepo"
              }
              color="primary"
              variant="outlined"
              size="small"
            />
            <Chip
              label={analysis.language}
              color="primary"
              variant="outlined"
              size="small"
            />
            <Chip
              label={analysis.framework}
              color="primary"
              variant="outlined"
              size="small"
            />
          </Stack>

          {/* Detected services */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              DETECTED SERVICES ({analysis.services.length})
            </Typography>
            {analysis.services.map((svc) => (
              <Box
                key={svc.name}
                sx={{
                  py: 1,
                  px: 2,
                  mb: 1,
                  borderRadius: 1,
                  bgcolor: "action.hover",
                }}
              >
                <Typography variant="body2" fontWeight={600} component="span">
                  {svc.name}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  component="span"
                  sx={{ ml: 2 }}
                >
                  {svc.type}
                  {svc.port ? ` · :${svc.port}` : ""}
                  {svc.dockerfilePath ? ` · ${svc.dockerfilePath}` : ""}
                </Typography>
                {svc.healthEndpoint && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ display: "block", fontSize: 13 }}
                  >
                    Health: {svc.healthEndpoint} · Internet-facing:{" "}
                    {svc.internetFacing ? "yes" : "no"}
                  </Typography>
                )}
              </Box>
            ))}
          </Paper>

          {/* Contract checks */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              STRUCTURE CONTRACT CHECKS
            </Typography>
            {analysis.contractChecks.map((check) => (
              <Box
                key={check.id}
                sx={{ display: "flex", alignItems: "center", py: 0.5 }}
              >
                {check.passed ? (
                  <CheckCircleIcon
                    color="success"
                    sx={{ fontSize: 18, mr: 1.5 }}
                  />
                ) : check.severity === "error" ? (
                  <ErrorIcon color="error" sx={{ fontSize: 18, mr: 1.5 }} />
                ) : (
                  <WarningIcon color="warning" sx={{ fontSize: 18, mr: 1.5 }} />
                )}
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ minWidth: 160 }}
                >
                  {check.id}
                </Typography>
                <Typography
                  variant="body2"
                  color={
                    check.passed
                      ? "text.secondary"
                      : check.severity === "error"
                        ? "error.main"
                        : "text.secondary"
                  }
                >
                  {check.message}
                </Typography>
              </Box>
            ))}
          </Paper>

          {/* Restructuring guide (if errors) */}
          {analysis.restructuringGuide &&
            analysis.restructuringGuide.length > 0 && (
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderColor: "error.main",
                  borderWidth: 1,
                  bgcolor: "rgba(244,63,94,0.04)",
                }}
              >
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  color="error.main"
                  gutterBottom
                >
                  {errorChecks.length} BLOCKER
                  {errorChecks.length > 1 ? "S" : ""} PREVENTING ADOPTION
                </Typography>
                {analysis.restructuringGuide.map((item, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      mt: 2,
                      pl: 1,
                      borderLeft: 2,
                      borderColor:
                        item.severity === "error"
                          ? "error.main"
                          : "warning.main",
                    }}
                  >
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      color={
                        item.severity === "error"
                          ? "error.main"
                          : "warning.main"
                      }
                    >
                      {item.severity === "error" ? "⛔" : "⚠"} {item.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.5 }}
                    >
                      {item.reason}
                    </Typography>
                    {item.codeSnippet && (
                      <Box
                        component="pre"
                        sx={{
                          mt: 1,
                          p: 1,
                          borderRadius: 1,
                          bgcolor: "action.hover",
                          fontSize: 13,
                          fontFamily: "monospace",
                          overflow: "auto",
                        }}
                      >
                        {item.codeSnippet}
                      </Box>
                    )}
                  </Box>
                ))}
                <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleInspect}
                  >
                    ↻ Re-scan Repository
                  </Button>
                  {!canAdopt && errorChecks.length === 0 && (
                    <Button
                      variant="outlined"
                      size="small"
                      color="inherit"
                      onClick={() => setActiveStep(2)}
                    >
                      Skip warnings & adopt
                    </Button>
                  )}
                </Stack>
              </Paper>
            )}

          {/* Generated rusha.yml (if passes) */}
          {analysis.rushaYml && canAdopt && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                <Typography variant="subtitle2" fontWeight={700}>
                  GENERATED rusha.yml
                </Typography>
                <Link
                  component="button"
                  variant="body2"
                  onClick={() =>
                    navigator.clipboard.writeText(analysis.rushaYml ?? "")
                  }
                >
                  Copy
                </Link>
              </Stack>
              <Box
                component="pre"
                sx={{
                  p: 2,
                  borderRadius: 1,
                  bgcolor: "action.hover",
                  fontSize: 13,
                  fontFamily: "monospace",
                  overflow: "auto",
                  maxHeight: 300,
                }}
              >
                {analysis.rushaYml}
              </Box>
            </Paper>
          )}
        </Stack>
      )}

      {/* Step 2: Adopt */}
      {activeStep === 2 && analysis && (
        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Stack name
          </Typography>
          <TextField
            fullWidth
            value={stackName}
            onChange={(e) => setStackName(e.target.value)}
            sx={{ mb: 3 }}
          />

          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Deploy mode
          </Typography>
          <RadioGroup
            value={deployMode}
            onChange={(e) => setDeployMode(e.target.value)}
            sx={{ mb: 3 }}
          >
            <FormControlLabel
              value="rusha_managed"
              control={<Radio />}
              label="Rusha Managed (build + deploy)"
            />
            <FormControlLabel
              value="ci_build_rusha_deploy"
              control={<Radio />}
              label="CI Build, Rusha Deploy"
            />
            <FormControlLabel
              value="ci_managed"
              control={<Radio />}
              label="CI Managed (bring your own)"
            />
          </RadioGroup>

          <Typography variant="body2" color="text.secondary" gutterBottom>
            rusha.yml will be committed to:
          </Typography>
          <Typography variant="body2" fontFamily="monospace" sx={{ mb: 3 }}>
            {analysis.repoFullName} @ {analysis.defaultBranch}
          </Typography>

          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Adoption steps
          </Typography>
          <Box sx={{ pl: 1 }}>
            <Typography variant="body2" color="text.secondary">
              ● Push rusha.yml to repo
            </Typography>
            <Typography variant="body2" color="text.secondary">
              ● Install push webhook
            </Typography>
            <Typography variant="body2" color="text.secondary">
              ● Create Application record
            </Typography>
          </Box>

          {adoptError && (
            <Alert
              severity="error"
              sx={{ mt: 2 }}
              onClose={() => setAdoptError(null)}
            >
              {adoptError}
            </Alert>
          )}
        </Paper>
      )}

      {/* Navigation buttons */}
      <Stack direction="row" justifyContent="space-between" sx={{ mt: 2 }}>
        <Button
          variant="outlined"
          onClick={activeStep === 0 ? handleCancel : handleBack}
        >
          {activeStep === 0 ? "Cancel" : "Back"}
        </Button>
        {activeStep === 1 && analysis && canAdopt && (
          <Button variant="contained" onClick={() => setActiveStep(2)}>
            Adopt →
          </Button>
        )}
        {activeStep === 2 && (
          <Button
            variant="contained"
            onClick={handleAdopt}
            disabled={adopting || !stackName.trim()}
          >
            {adopting ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Adopt & Deploy"
            )}
          </Button>
        )}
      </Stack>
    </Container>
  );
}
